import { Fragment } from 'react';
import { Popover } from '@headlessui/react';
import { useMedia } from 'react-use';
import {
  Beef,
  Pescatarian,
  Vegetarian,
  Vegan,
  CaretDown,
  Check,
  Flexitarian,
} from '@hungryroot/icons';
import { EatingHabit } from '@hungryroot/quiz';
import { Paragraph, Heading } from '@hungryroot/ramen';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import { Sheet } from '../../../../components/Sheet/Sheet';
import { capitalizeFirstLetter } from '../DietaryNeeds/utils';

import styles from './DietaryPopover.module.css';

interface Props {
  testid?: string;
  active: EatingHabit;
  onClick: (item: EatingHabit) => void;
}

function dietaryRestrictions() {
  return [
    {
      value: EatingHabit.Omnivore,
      label: 'I eat meat + seafood most meals',
      icon: <Beef />,
    },
    {
      value: EatingHabit.Flexitarian,
      label: 'I eat meat + seafood some meals',
      icon: <Flexitarian />,
    },
    {
      value: EatingHabit.Pescatarian,
      label: "I don't eat meat",
      icon: <Pescatarian />,
    },
    {
      value: EatingHabit.Vegetarian,
      label: "I don't eat meat or seafood",
      icon: <Vegetarian />,
    },
    {
      value: EatingHabit.Vegan,
      label: "I don't eat meat, seafood, dairy, or eggs",
      icon: <Vegan />,
    },
  ];
}

export default function DietaryPopover(props: Props) {
  const { testid, active, onClick } = props;
  const isDesktop = useMedia(BreakpointQueryLarge);

  const restrictions = dietaryRestrictions();

  const activeDiet = restrictions.find((item) => item.value === active);

  const content = (
    <ul className={styles.list}>
      {restrictions.map((item) => {
        return (
          <li
            data-testid={`${testid}-item-${item.value}`}
            key={item.value}
            className={styles.item}
            onClick={() => {
              onClick(item.value);
            }}
          >
            <div className={styles['item-body']}>
              {item.icon}
              <div>
                <Heading variant="H5">
                  {capitalizeFirstLetter(item.value)}
                </Heading>
                <Paragraph className={styles.label}>{item.label}</Paragraph>
              </div>
            </div>
            {item.value === active ? (
              <div
                data-testid={`${testid}-item-${item.value}-active`}
                className={styles['item-active']}
              >
                <Check />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );

  return (
    <Popover>
      {({ open, close }) => {
        return (
          <Fragment key="dietary-popover">
            <Popover.Button
              data-testid={`${testid}-button`}
              className={styles.button}
            >
              <ActiveDietaryRestriction
                activeDiet={activeDiet}
                testid={testid}
              />
            </Popover.Button>
            {isDesktop === false ? (
              <Popover.Panel
                as={Sheet}
                testid={`${testid}-panel-mobile`}
                ariaLabel="dietary-popover-sheet"
                onRequestDismiss={() => close()}
                active={open}
                initialFocusRef={undefined}
                static
              >
                {content}
              </Popover.Panel>
            ) : (
              <Popover.Panel
                data-testid={`${testid}-panel-desktop`}
                className={styles.panel}
              >
                {content}
              </Popover.Panel>
            )}
          </Fragment>
        );
      }}
    </Popover>
  );
}

function ActiveDietaryRestriction({
  activeDiet,
  testid,
}: {
  activeDiet:
    | { value: EatingHabit; label: string; icon: JSX.Element }
    | undefined;
  testid?: string;
}) {
  if (!activeDiet) {
    return null;
  }

  return (
    <Fragment key="button">
      <div className={styles['button-body']}>
        {activeDiet.icon}
        <div>
          <Heading testid={`${testid}-button-heading`} variant="H5">
            {capitalizeFirstLetter(activeDiet.value)}
          </Heading>
          <Paragraph className={styles.label}>{activeDiet.label}</Paragraph>
        </div>
      </div>
      <div className={styles['button-icon']}>
        <CaretDown />
      </div>
    </Fragment>
  );
}
